import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { Ref, RefObject } from 'react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import type { TeamMemberScope } from '~publish/hooks/notes/selectableTeamMembersQuery'

type PostNotesFrame = {
  providerProps: {
    postId: string
    canCreate: boolean
    mentionScope: TeamMemberScope | null
    focusAfterDelete: () => void
  }
  infoButtonRef: RefObject<HTMLButtonElement | null>
  createFieldRef: Ref<HTMLDivElement>
  focusAfterDeleteRequested: boolean
  clearFocusAfterDelete: () => void
  focusCreate: () => void
}

/**
 * Provider arguments and composer focus shared by the side panel and the
 * queue popover. Each caller renders its own header.
 */
export function usePostNotesFrame({
  postId,
  channelId,
  canCreate,
  focusComposer = false,
  hidden = false,
  fallbackRef,
}: {
  postId: string
  channelId?: string | null
  canCreate: boolean
  focusComposer?: boolean
  hidden?: boolean
  fallbackRef?: RefObject<HTMLElement | null>
}): PostNotesFrame {
  const infoButtonRef = useRef<HTMLButtonElement>(null)
  const createFieldRef = useRef<HTMLDivElement>(null)
  const [focusAfterDeleteRequested, setFocusAfterDeleteRequested] =
    useState(false)
  const organizationId = useOrganizationId()

  const focusCreate = (): void => {
    ;(
      createFieldRef.current ??
      infoButtonRef.current ??
      fallbackRef?.current
    )?.focus()
  }

  // The composer only renders with addPostNote. Callers that suppress the
  // overlay's own open focus need this fallback, or a viewer without that
  // action is left outside the notes UI.
  useLayoutEffect(() => {
    if (hidden || !focusComposer) return
    ;(
      createFieldRef.current ??
      infoButtonRef.current ??
      fallbackRef?.current
    )?.focus()
  }, [fallbackRef, focusComposer, hidden])

  const mentionScope: TeamMemberScope | null = channelId
    ? { channelId }
    : organizationId
      ? { organizationId }
      : null

  const focusAfterDelete = useCallback((): void => {
    setFocusAfterDeleteRequested(true)
  }, [])
  const clearFocusAfterDelete = useCallback((): void => {
    setFocusAfterDeleteRequested(false)
  }, [])

  return {
    providerProps: {
      postId,
      canCreate,
      mentionScope,
      focusAfterDelete,
    },
    infoButtonRef,
    createFieldRef: createFieldRef as Ref<HTMLDivElement>,
    focusAfterDeleteRequested,
    clearFocusAfterDelete,
    focusCreate,
  }
}
